import { motion } from 'framer-motion';
import { Camera, Ruler, Zap, Video, Plane, Box, Flower2 } from 'lucide-react';
import { useSceneTime, seg, EASE } from '../engine/sceneTime';
import { Words, Fade, Line, Eyebrow } from '../components/Kinetic';
import { Vid } from '../components/Media';
import { CountUp, fmtInt } from '../components/CountUp';
import { IMAGES } from '../assets';

// ── 01 OPENING ──────────────────────────────────────────────────
export function Opening({ scene }) {
  const { t } = useSceneTime();
  const lineDrawn = t >= 0.4;
  const open = t >= 2.4;
  const headline = t >= 4.2;
  const sub = t >= 9;
  const settle = t >= 15;
  return (
    <div className="pk-fill">
      <motion.div
        className="pk-fill pk-montage"
        initial={false}
        animate={{ clipPath: open ? 'inset(0% 0% 0% 0%)' : 'inset(49.7% 0% 49.7% 0%)', scale: settle ? 1.07 : 1 }}
        transition={{ clipPath: { duration: 1.7, ease: EASE }, scale: { duration: 5, ease: 'easeOut' } }}
      >
        <Vid video={scene.media.video} className="pk-fill" />
        <div className="pk-montage-shade" />
      </motion.div>
      <motion.div
        className="pk-abs pk-line pk-line-glow"
        style={{ left: 0, top: 537, width: 1920, height: 5, transformOrigin: 'left' }}
        initial={false}
        animate={{ scaleX: lineDrawn ? 1 : 0, opacity: open ? 0 : 1 }}
        transition={{ scaleX: { duration: 1.9, ease: EASE }, opacity: { duration: 0.9, delay: open ? 0.5 : 0 } }}
      />
      <div className="pk-abs" style={{ left: 120, top: 330, width: 1560 }}>
        <Eyebrow show={headline} delay={0.1}>Since 1993 · Property marketing</Eyebrow>
        <Words text={scene.headline} show={headline} size={136} lime={[0, 1]} style={{ marginTop: 18 }} />
        <Fade show={sub} y={22} blur className="pk-x-row" style={{ marginTop: 54 }}>
          <img src={IMAGES.nichecomLogoNav.url} alt="NicheCom" style={{ height: 64 }} />
          <span className="pk-x">×</span>
          <img src={IMAGES.jacksonStopsLogo.url} alt="Jackson-Stops" className="pk-js-logo" style={{ height: 150 }} />
        </Fade>
      </div>
      <Line show={settle} width={1680} style={{ position: 'absolute', left: 120, top: 980, opacity: 0.6 }} />
    </div>
  );
}

// ── 02 SERVICES ─────────────────────────────────────────────────
const SERVICE_ICONS = { Photography: Camera, 'Lifestyle imagery': Flower2, Floorplans: Ruler, EPCs: Zap, Video, Drone: Plane, 'Matterport / 3D': Box };

export function Services({ scene }) {
  const { t } = useSceneTime();
  // Ring sits right of the headline; angles start at -64° so no pill lands over the copy on the left.
  const cx = 1250; const cy = 560; const rx = 465; const ry = 358; const R = 285;
  const angle = (i) => -Math.PI / 2 + ((i + 0.5) / scene.services.length) * Math.PI * 2;
  const centre = t >= 0.3;
  const headline = t >= 1.2;
  return (
    <div className="pk-fill">
      <div className="pk-abs" style={{ left: 120, top: 150, width: 660 }}>
        <Eyebrow show={headline}>What NicheCom does</Eyebrow>
        <Words text={scene.headline} show={headline} size={76} lime={[3]} style={{ marginTop: 16 }} />
        <Fade show={t >= 15} y={16} className="pk-sub" style={{ marginTop: 30, maxWidth: 520 }}>One visit. Every asset.</Fade>
      </div>
      <svg className="pk-abs" width={1920} height={1080} style={{ left: 0, top: 0, pointerEvents: 'none' }}>
        {scene.services.map((s, i) => {
          const a = angle(i);
          const x = cx + Math.cos(a) * rx; const y = cy + Math.sin(a) * ry;
          const on = t >= 2 + i * 1.15;
          return (
            <motion.line key={s} x1={cx + Math.cos(a) * R} y1={cy + Math.sin(a) * R} x2={x} y2={y} stroke="rgba(150,193,31,0.45)" strokeWidth={2}
              initial={false} animate={{ pathLength: on ? 1 : 0, opacity: on ? 1 : 0 }} transition={{ duration: 1, ease: EASE }} />
          );
        })}
        <motion.ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={1.5} strokeDasharray="6 10"
          initial={false} animate={{ pathLength: t >= 1 ? 1 : 0 }} transition={{ duration: 2.2, ease: EASE }} />
      </svg>
      <motion.div className="pk-abs pk-centre-property" style={{ left: cx - R, top: cy - R, width: R * 2, height: R * 2 }}
        initial={false} animate={{ scale: centre ? 1 : 0.4, opacity: centre ? 1 : 0, rotate: t >= 15 ? 2 : 0 }} transition={{ duration: 1.3, ease: EASE }}>
        {scene.media.centreImages ? (
          scene.media.centreImages.map((img, i) => {
            const n = scene.media.centreImages.length; const per = 3.6;
            const idx = t < 2 ? 0 : Math.floor((t - 2) / per) % n;
            const on = idx === i;
            return (
              <motion.img key={img.url} src={img.url} alt="" className="pk-centre-still" initial={false}
                animate={{ opacity: on ? 1 : 0, scale: on ? 1.07 : 1 }} transition={{ opacity: { duration: 1.1, ease: 'easeInOut' }, scale: { duration: per + 1.2, ease: 'linear' } }} />
            );
          })
        ) : scene.media.centreVideo
          ? <Vid video={scene.media.centreVideo} className="pk-centre-vid" active={centre} />
          : <img src={scene.media.centre.url} alt="" />}
        <div className="pk-centre-ring" />
        <div className="pk-centre-badge"><Camera size={26} /></div>
        {scene.media.centreTag && <Fade show={t >= 2.4} y={8} className="pk-centre-tag"><span>{scene.media.centreTag}</span></Fade>}
      </motion.div>
      {scene.services.map((s, i) => {
        const a = angle(i);
        const x = cx + Math.cos(a) * rx; const y = cy + Math.sin(a) * ry;
        const on = t >= 2 + i * 1.15;
        const Icon = SERVICE_ICONS[s] || Camera;
        return (
          <motion.div key={s} className="pk-glass pk-service" style={{ left: x - 130, top: y - 46 }}
            initial={false}
            animate={{ opacity: on ? 1 : 0, scale: on ? 1 : 0.6, x: on ? 0 : Math.cos(a) * 160, y: on ? 0 : Math.sin(a) * 160 }}
            transition={{ duration: 0.9, ease: EASE }}>
            <span className="pk-service-icon"><Icon size={26} /></span>
            <span>{s}</span>
          </motion.div>
        );
      })}
    </div>
  );
}

// ── 03 SCALE ────────────────────────────────────────────────────
const GRID_COLS = 10; const GRID_ROWS = 7;
const GRID = Array.from({ length: GRID_COLS * GRID_ROWS }, (_, i) => ({ i, order: (i * 7919) % (GRID_COLS * GRID_ROWS), drift: 6 + (i % 5) * 2 }));

export function Scale({ scene }) {
  const { t } = useSceneTime();
  const thumbs = scene.media.thumbs;
  const countP = seg(t, 1.2, 7.5);
  const revealed = Math.floor(seg(t, 0.2, 9) * GRID.length);
  return (
    <div className="pk-fill">
      <div className="pk-abs pk-thumb-grid" style={{ left: -40, top: -40, width: 2000, height: 1160 }}>
        {GRID.map(({ i, order, drift }) => {
          const on = order < revealed;
          const img = thumbs[(i * 3 + Math.floor(i / GRID_COLS)) % thumbs.length];
          return (
            <motion.div key={i} className="pk-thumb" initial={false} animate={{ opacity: on ? 0.9 : 0, scale: on ? 1 : 0.7 }} transition={{ duration: 0.6, ease: EASE }}>
              <img src={img.url} alt="" loading="eager" style={{ animationDuration: `${drift}s`, animationPlayState: on ? 'running' : 'paused' }} />
            </motion.div>
          );
        })}
      </div>
      <div className="pk-abs pk-scale-shade" />
      <div className="pk-abs" style={{ left: 0, top: 250, width: 1920, textAlign: 'center' }}>
        <Eyebrow show={t >= 1}>Every year</Eyebrow>
        <div className="pk-giant">
          <CountUp to={scene.number} progress={countP} format={fmtInt} />
        </div>
        <Words text={scene.sub} show={t >= 7.8} size={64} className="pk-words-center pk-light" stagger={0.12} />
      </div>
      <div className="pk-abs pk-pipeline" style={{ left: 260, top: 860, width: 1400 }}>
        {scene.pipeline.map((p, i) => {
          const on = t >= 10 + i * 1.4;
          const Icon = [Camera, Ruler, Video, Zap][i];
          return (
            <div key={p} className="pk-pipe-step">
              <motion.div className="pk-glass pk-pipe-node" initial={false} animate={{ opacity: on ? 1 : 0, y: on ? 0 : 30 }} transition={{ duration: 0.7, ease: EASE }}>
                <Icon size={30} /><span>{p}</span>
              </motion.div>
              {i < scene.pipeline.length - 1 && (
                <motion.div className="pk-pipe-link" initial={false} animate={{ scaleX: t >= 10.7 + i * 1.4 ? 1 : 0 }} transition={{ duration: 0.8, ease: EASE }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
